import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useErrorHandler } from "react-error-boundary";
import { Controller, useForm } from "react-hook-form";
import ReactInputMask from "react-input-mask";
import Select from "react-select";
import { z } from "zod";

import { ActionButton } from "../../../fkc/components/shared/submit-button/submit-button.component";
import { updateFormioSubmission } from "../../../shared/api/yenta-api";
import { getEnvironment, validatePhoneNumber } from "../../../shared/utils";
import programOptions from "../../constants/programs.json";
import { SNHU_EXTRA_ENTRIES } from "../../constants/snhu.constants";
import stateOptions from "../../constants/states.json";
import { useTrustedForm } from "../../hooks/useTrustedForm";
import { SNHUService } from "../../snhu.service";
import { pagesAtom } from "../../snhu.state";
import { Pages } from "../../snhu.types";

interface SurveyPageProps {
  skipSnhu: VoidFunction;
}

export type SNHUFormData = z.infer<typeof formSchema>;

const formSchema = z.object({
  ProgramofInterest: z.string().min(1, { message: "Program of Interest is required" }),
  FirstName: z.string().min(1, { message: "First Name is required" }),
  LastName: z.string().min(1, { message: "Last Name is required" }),
  Address: z.string().min(1, { message: "Address is required" }),
  City: z.string().min(1, { message: "City is required" }),
  Zip: z.string(),
  Email: z.string().email().min(1),
  Phonenumber: z.string()
    .min(1, { message: "Phone number is required" })
    .refine(validatePhoneNumber, {
      message: "Invalid phone number",
    }),
  State: z.string().min(1, { message: "State is required" }),
  TrustedFormCert: z.string().optional(),
});

export function SurveyPage({ skipSnhu }: SurveyPageProps): JSX.Element {
  const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
  const email: Optional<string> = submission?.data?.email;
  const zipCode: Optional<string> = submission?.data?.zipCode;
  const formId = "snhu-form";

  const [, setPage] = useAtom(pagesAtom);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const handleWidgetError = useErrorHandler();
  const trustedFormCertUrl = useTrustedForm({ formId, sandbox: getEnvironment() !== "production" });

  const {
    reset,
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<SNHUFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      Email: email,
      Zip: zipCode,
    },
  });

  const goToConfirmation = useCallback(() => {
    setPage(Pages.CONFIRMATION);
  }, [setPage]);

  const postLead = useCallback((data: SNHUFormData) => {
    setIsLoading(true);

    const sweepFormUrl =
      document
        .querySelector("#FormSweepstake")
        ?.getAttribute("data-form-path-url") ?? "";

    SNHUService
      .postLead(data, setError)
      .then(() =>
        updateFormioSubmission(
          sweepFormUrl,
          {
            is_cpl_in_flow: true,
            cpl_in_flow_extra_entries: SNHU_EXTRA_ENTRIES,
            ...submission,
          },
          handleWidgetError
        )
      )
      .then(window.theme.Sweepstake.prototype.storeSubmission)
      .then(window.theme.LyticsService.snhuCompletionEventTracker)
      .then(window.theme.SnhuUtils.saveCompletionInSession)
      .then(goToConfirmation)
      .catch((error) => {
        setIsLoading(false);

        if (error) {
          handleWidgetError(error);
        }
      });
  }, [handleWidgetError, submission, setError, goToConfirmation]);


  useEffect(() => {
    if (trustedFormCertUrl.length > 0) {
      reset({
        TrustedFormCert: trustedFormCertUrl,
      });
    }
  }, [trustedFormCertUrl, reset]);

  return (
    <div className="snhu__children__container">
      <div className="snhu__children">
        <div className="snhu__children--entries-box">
          +{SNHU_EXTRA_ENTRIES} Entries
        </div>
        <p className="snhu__children--title">
          Fantastic! Would you like to receive more online degree information from
          Southern New Hampshire University for {SNHU_EXTRA_ENTRIES} extra entries?
        </p>
      </div>

      <form id={formId} className="snhu__form" onSubmit={handleSubmit(postLead)}>
        <div className="snhu__form--input-group">
          <Controller
            control={control}
            name="ProgramofInterest"
            render={({ field: { onChange } }) => (
              <Select
                className={clsx("snhu__form--input", "select-input")}
                placeholder="Program of Interest"
                options={programOptions}
                onChange={(selected) => onChange(selected?.value)}
              />
            )}
          />
        </div>

        <input
          className="snhu__form--input"
          type="email"
          placeholder="Email"
          hidden={!!email}
          {...register("Email")}
        />

        <div className="snhu__form--input-group">
          <input
            type="text"
            placeholder="First Name"
            className="snhu__form--input"
            {...register("FirstName")}
          />
          <input
            type="text"
            placeholder="Last Name"
            className="snhu__form--input"
            {...register("LastName")}
          />
        </div>

        <div className="snhu__form--input-group">
          <ReactInputMask
            mask="(999) 999-9999"
            placeholder="Phone Number"
            className="snhu__form--input"
            {...register("Phonenumber")}
          />
        </div>

        <div className="snhu__form--input-group">
          <input
            type="text"
            placeholder="City"
            className="snhu__form--input"
            {...register("City")}
          />
          <Controller
            control={control}
            name="State"
            render={({ field: { onChange } }) => (
              <Select
                className={clsx("snhu__form--input", "select-input")}
                placeholder="State"
                options={stateOptions}
                onChange={(selected) => onChange(selected?.value)}
              />
            )}
          />
        </div>

        <div className="snhu__form--input-group">
          <input
            type="text"
            placeholder="Address"
            className="snhu__form--input"
            {...register("Address")}
          />

          <input
            className="snhu__form--input"
            type="text"
            placeholder="Zip Code"
            hidden={!!zipCode}
            {...register("Zip")}
          />
        </div>

        {Object.keys(errors).length > 0 && (
          <ul className="snhu__form--error">
            {Object.values(errors).map((error) => (
              <li key={error.message}>{error.message}</li>
            ))}
          </ul>
        )}

        <span className="snhu__tnc">
          By clicking &quot;Sign me up&quot;, I provide my elec. sig. and agree
          to receive marketing emails, phone calls, and text (SMS/MMS) messages
          from Southern New Hampshire University (SNHU) at the number I provided
          above including automated calls or texts made using prerecorded and/or
          artificial voice messages. This consent is not required to purchase
          services, you may also call SNHU at 800.668.1249.&nbsp;
          <a href="https://www.snhu.edu/privacy-policy/sms-terms-and-conditions" target="_blank" rel="noreferrer">
            SMS Terms and Conditions apply.
          </a>
        </span>

        <div className={clsx("lander__cpl__quiz__body", "snhu__content--buttons-body")}>
          <div className={clsx("lander__cpl__quiz__body--question-container", "no-margin")}>
            <div className="lander__cpl__quiz__body--question-actions">
              <div className={clsx("lander__cpl__quiz__body--button-container", "no-padding-top")}>
                <ActionButton type="submit" className="full-width" disabled={isLoading}>
                  Sign me up!
                </ActionButton>

                <button
                  className={clsx("lander-quiz--button", "snhu__content--buttons-skip")}
                  onClick={skipSnhu}>
                  No thanks
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
